import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Checkbox,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import ProjectSearchableAutocomplete from "./tabs/ProjectSearchableAutoComplete";
import { SearchableEntity } from "../services/searchServiceProject";
import { formatText } from "../../../utils/utils";
import { commonButtonProps, commonTextFieldProps } from "../../../utils/styles";
import { Formik } from "formik";
import * as yup from "yup";

const ReAllocateModal = ({
  isModalOpen,
  setIsModalOpen,
  projectId,
  allocationId,
  selectedStaff,
  searchableEntity,
  updateStaffMembers,
}: {
  isModalOpen: boolean;
  setIsModalOpen: (isModalOpen: boolean) => void;
  projectId: string;
  allocationId: string;
  selectedStaff: any;
  searchableEntity: SearchableEntity;
  updateStaffMembers?: (
    oldStaffId: string,
    newStaff: any,
    isReplacement?: boolean,
    noOfDays?: number
  ) => void;
}) => {
  const getDisplayValue = (option: any) => {
    if (typeof option === "string") return option;
    return `${option.name} - ${
      option.phoneNumber || option.contactNumber || "N/A"
    }`;
  };

  const handleValueSelect = (
    option: any,
    setFieldValue: (field: string, value: any) => void
  ) => {
    setFieldValue("newStaff", { ...option, type: searchableEntity });
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const validationSchema = yup.object().shape({
    newStaff: yup
      .object()
      .shape({
        id: yup.string().required("New staff is required"),
        name: yup.string().required("New staff is required"),
      })
      .required("Select a staff"),
    replace: yup.boolean().required("Replace is required"),
    noOfDays: yup.number().when("replace", {
      is: false,
      then: (schema) =>
        schema
          .required("No of days is required")
          .min(1, "No of days must be greater than 0")
          .max(365, "No of days must be less than 365"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
  });

  return (
    <Dialog
      open={isModalOpen}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>Change {formatText(searchableEntity)}</DialogTitle>
      <DialogContent>
        <Formik
          initialValues={{
            allocationId: allocationId,
            oldStaff: selectedStaff,
            newStaff: null,
            replace: false,
            noOfDays: 1,
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            // Update the staff members display list and attendance
            if (updateStaffMembers) {
              updateStaffMembers(
                selectedStaff.id,
                values.newStaff,
                values.replace,
                values.noOfDays
              );
            }

            handleClose();
          }}
          validateOnChange={true}
          validateOnBlur={true}
        >
          {({ values, errors, touched, setFieldValue, handleSubmit }) => {
            return (
              <Grid container mt={1} spacing={2}>
                <Grid item xs={12}>
                  <ProjectSearchableAutocomplete
                    entityType={searchableEntity}
                    value={getDisplayValue(values.newStaff || "")}
                    onSelect={(option) =>
                      handleValueSelect(option, setFieldValue)
                    }
                    onChange={() => {}}
                    label={`Select ${formatText(searchableEntity)}`}
                    placeholder={`Search ${formatText(searchableEntity)}...`}
                    getOptionLabel={(option) => getDisplayValue(option)}
                    renderOptionContent={(option) => getDisplayValue(option)}
                    size="small"
                    projectId={projectId}
                    allocationId={allocationId}
                    error={touched.newStaff && !!errors.newStaff}
                    helperText={
                      Boolean(touched.newStaff && errors.newStaff)
                        ? errors.newStaff
                        : undefined
                    }
                  />
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    label="For Next No of Days"
                    type="number"
                    value={values.noOfDays}
                    onChange={(e) =>
                      setFieldValue("noOfDays", e.target.value)
                    }
                    inputProps={{ min: 1, max: 365 }}
                    fullWidth
                    disabled={values.replace}
                    error={touched.noOfDays && !!errors.noOfDays}
                    helperText={touched.noOfDays && errors.noOfDays}
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={7}>
                  <FormControlLabel
                    control={
                      <Checkbox size="small" disabled={!values.newStaff} />
                    }
                    labelPlacement="end"
                    label={
                      <Typography sx={{ fontSize: "0.75rem" }}>
                        Replace {formatText(searchableEntity)}
                      </Typography>
                    }
                    checked={values.replace}
                    onChange={(e: any) => {
                      setFieldValue("replace", e.target.checked);
                      if (e.target.checked) {
                        setFieldValue("noOfDays", 0);
                      } else {
                        setFieldValue("noOfDays", 1);
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" gap={1} justifyContent="flex-end">
                    <Button
                      onClick={handleClose}
                      variant="outlined"
                      color="error"
                      {...commonButtonProps}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleSubmit()}
                      {...commonButtonProps}
                    >
                      Change
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default ReAllocateModal;
